import { RefreshCw } from "lucide-react";
import Skeleton from "../Skeleton";
import Button from "../Button";
import "./_stat-cards.scss";

// Matches the design's own per-index skeleton value widths exactly, so a
// loading row of cards varies in width the same way the design's does
// instead of every card showing an identical bar.
const SKELETON_VALUE_WIDTHS = ["64px", "88px", "72px", "56px", "80px"];

/**
 * A row of metric cards (label + big value + small delta/sub-line) for a
 * page header - the design's Net MRR/Active accounts/etc. cards. Distinct
 * from `SimpCard` (no delta slot, different value/label order) rather than
 * overloading it.
 *
 * Each item: { label, value, sub, tone, valueTone, isLoading }. `value`/
 * `sub` pass straight through - a caller with no real number for a card
 * yet should pass "—" rather than this component inventing a placeholder,
 * so a card only ever shows a number the page can vouch for. `tone`
 * colors `sub` (green/red/amber/undefined -> neutral), `valueTone` colors
 * `value` the same way (used for e.g. an at-risk count in red).
 *
 * `variant="strip"` (default "grid"): the detail-view stat strip - one
 * glass bar with inline stat groups instead of a grid of individually
 * bordered/shadowed cards. Same items shape, same markup; `--stat-cards.scss`
 * moves the glass chrome from each card up to the container.
 *
 * `onRefresh` (optional): renders a small icon-only refresh control on the
 * right of the row - for pages whose numbers are fetched once and cached
 * (see `_hooks/useOrgStats`) rather than always live, so there's an
 * explicit way to pull latest values on demand instead of only on next
 * page load. `isRefreshing` spins the icon while a refresh is in flight;
 * omit `onRefresh` entirely for a page with no such control.
 */
const StatCards = ({ items = [], variant = "grid", onRefresh, isRefreshing = false }) => (
  <div className="stat-cards-row">
    <div
      className={`stat-cards${variant === "strip" ? " stat-cards--strip" : ""}`}
      style={{ "--stat-count": items.length }}
    >
      {items.map((item, idx) => (
        <div className="stat-cards__card" key={item.label ?? idx}>
          {item.isLoading ? (
            <Skeleton width="70px" height="11px" />
          ) : (
            <p className="stat-cards__label">{item.label}</p>
          )}
          <div className="stat-cards__value-row">
            {item.isLoading ? (
              <Skeleton width={SKELETON_VALUE_WIDTHS[idx % SKELETON_VALUE_WIDTHS.length]} height="22px" radius="5px" />
            ) : (
              <span className={`stat-cards__value stat-cards__value--${item.valueTone || "neutral"}`}>
                {item.value}
              </span>
            )}
            {item.sub && !item.isLoading && (
              <span className={`stat-cards__sub stat-cards__sub--${item.tone || "neutral"}`}>
                {item.sub}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
    {onRefresh && (
      <Button
        variant="outlined"
        size="sm"
        iconOnly
        icon={<RefreshCw size={16} className={isRefreshing ? "stat-cards-row__refresh-icon--spinning" : undefined} />}
        className="stat-cards-row__refresh-btn"
        onClick={onRefresh}
        disabled={isRefreshing}
        title="Refresh numbers"
        type="button"
      />
    )}
  </div>
);

export default StatCards;
